import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

export type ViewportNotificationTone = "error" | "success";

type ViewportNotificationProps = {
  message: string | null;
  tone: ViewportNotificationTone;
  onDismiss: () => void;
  /** When set, auto-dismiss after this many ms (typical for success). */
  autoDismissMs?: number;
};

export function ViewportNotification({
  message,
  tone,
  onDismiss,
  autoDismissMs,
}: ViewportNotificationProps) {
  useEffect(() => {
    if (!message || autoDismissMs == null) return;
    const timer = window.setTimeout(onDismiss, autoDismissMs);
    return () => window.clearTimeout(timer);
  }, [message, autoDismissMs, onDismiss]);

  if (!message) return null;

  const isError = tone === "error";

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center px-4 pt-3 sm:pt-4">
      <div
        role={isError ? "alert" : "status"}
        aria-live={isError ? "assertive" : "polite"}
        className={cn(
          "pointer-events-auto flex w-full max-w-xl items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ring-1 ring-black/5 dark:ring-white/10",
          isError
            ? "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/90 dark:text-amber-100"
            : "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800/50 dark:bg-emerald-950/90 dark:text-emerald-100",
        )}
      >
        <p className="min-w-0 flex-1 font-medium leading-snug">{message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            "shrink-0 rounded-md p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
            isError
              ? "text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/50"
              : "text-emerald-800 hover:bg-emerald-100 dark:text-emerald-200 dark:hover:bg-emerald-900/50",
          )}
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>,
    document.body,
  );
}
